import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/AuthContext';
import { API_BASE, s, authHeaders } from './sharedStyles';

export default function AccountManagementPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: '', password: '', callerPassword: '' });
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyUser, setBusyUser] = useState(null);

  // Change password
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);

  const loadAdmins = useCallback(() => {
    fetch(`${API_BASE}/api/v1/admin/admins`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : [])
      .then(setAdmins)
      .catch((e) => console.error('Admins fetch failed', e));
  }, [token]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/create-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg({ type: 'success', text: t('monitor.account.created') });
        setForm({ email: '', password: '', callerPassword: '' });
        loadAdmins();
      } else {
        setMsg({ type: 'error', text: data.message || data.error || t('monitor.account.failed') });
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'Network error' });
    } finally { setSaving(false); }
  };

  const toggleActive = async (userId) => {
    setBusyUser(userId);
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/users/${userId}/toggle-active`, {
        method: 'PATCH',
        headers: authHeaders(token),
      });
      if (res.ok) {
        const updated = await res.json();
        setAdmins((prev) => prev.map((a) => a.id === userId ? { ...a, active: updated.active } : a));
      }
    } catch (e) { console.error('Toggle admin active failed', e); }
    finally { setBusyUser(null); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pwForm.newPassword.length < 8) { setPwMsg({ type: 'error', text: t('monitor.account.pwMinLength') }); return; }
    if (pwForm.newPassword !== pwForm.confirm) { setPwMsg({ type: 'error', text: t('monitor.account.pwMismatch') }); return; }
    setPwSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword }),
      });
      if (res.ok) {
        setPwMsg({ type: 'success', text: t('monitor.account.pwChanged') });
        setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
      } else {
        const data = await res.json().catch(() => ({}));
        setPwMsg({ type: 'error', text: data.message || t('monitor.account.pwFailed') });
      }
    } catch { setPwMsg({ type: 'error', text: 'Network error' }); }
    finally { setPwSaving(false); }
  };

  return (
    <div>
      <main style={s.main}>
        <h1 style={s.pageTitle}>{t('monitor.sections.account')}</h1>

        <div style={s.card}>
          <div style={s.cardTitle}>{t('monitor.account.changePassword')}</div>
          {pwMsg && (
            <p style={{ color: pwMsg.type === 'error' ? '#F87171' : '#34D399', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              {pwMsg.text}
            </p>
          )}
          <form onSubmit={handleChangePassword}>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>{t('monitor.account.currentPw')}</label>
              <input type="password" required style={{ ...s.filterInput, marginBottom: '0.75rem' }}
                value={pwForm.currentPassword} onChange={(e) => setPwForm((f) => ({ ...f, currentPassword: e.target.value }))} />
            </div>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>{t('monitor.account.newPw')}</label>
              <input type="password" required minLength={8} placeholder="Min. 8 characters" style={{ ...s.filterInput, marginBottom: '0.75rem' }}
                value={pwForm.newPassword} onChange={(e) => setPwForm((f) => ({ ...f, newPassword: e.target.value }))} />
            </div>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>{t('monitor.account.confirmPw')}</label>
              <input type="password" required style={{ ...s.filterInput, marginBottom: '0.75rem' }}
                value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} />
            </div>
            <button type="submit" style={s.primaryBtn} disabled={pwSaving}>
              {pwSaving ? t('monitor.account.pwSaving') : t('monitor.account.pwSubmit')}
            </button>
          </form>
        </div>

        <div style={s.card}>
          <div style={s.cardTitle}>{t('monitor.account.adminUsers')}</div>
          {admins.length === 0 && <div style={{ color: '#64748B' }}>{t('monitor.account.noAdmins')}</div>}
          {admins.map((a) => (
            <div key={a.id} style={s.statusRow}>
              <span style={s.statusLabel}>{a.email}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ ...s.statusBadge(a.active) }}>{a.active ? t('monitor.tenants.active') : t('monitor.tenants.disabled')}</span>
                <button
                  style={s.freezeBtn(!a.active)}
                  disabled={busyUser === a.id}
                  onClick={() => toggleActive(a.id)}
                >
                  {a.active ? t('monitor.tenants.freeze') : t('monitor.tenants.unfreeze')}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div style={s.card}>
          <div style={s.cardTitle}>{t('monitor.account.createAdmin')}</div>
          {msg && (
            <p style={{ color: msg.type === 'error' ? '#F87171' : '#34D399', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              {msg.text}
            </p>
          )}
          <form onSubmit={handleCreate}>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>{t('monitor.account.email')}</label>
              <input type="email" required style={{ ...s.filterInput, marginBottom: '0.75rem' }}
                value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>{t('monitor.account.newPassword')}</label>
              <input type="password" required minLength={8} style={{ ...s.filterInput, marginBottom: '0.75rem' }}
                value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
            </div>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>{t('monitor.account.yourPassword')}</label>
              <input type="password" required style={{ ...s.filterInput, marginBottom: '0.75rem' }}
                value={form.callerPassword} onChange={(e) => setForm((f) => ({ ...f, callerPassword: e.target.value }))} />
            </div>
            <button type="submit" style={s.primaryBtn} disabled={saving}>
              {saving ? t('monitor.account.creating') : t('monitor.account.createBtn')}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
